import InsightsService from '@/services/InsightsServiceV2'
import moment from 'moment-timezone'
import { isEqual } from 'lodash-es'

export default {
  namespaced: true,
  state: {
    classifications: [],
    oppositeSentimentClassifications: [],
    oppositeSentimentMentions: {},
    mentions: {},
    detailFilters: {},
    populatedClassifications: [],
    positiveClassificationsByCategory: {},
    negativeClassificationsByCategory: {},
    feedbackCountBreakdown: {},
    feedbackCount: 0,
    skip: 0,
    limit: 20,
    count: 0,
    summaries: {},
    wordClouds: {},
    wordCloudQuantity: 40,
    feedbackBreakdown: [],
    totalFeedbackCount: 0
  },
  getters: {
    selectClassifications(state) {
      return state.classifications
    },
    selectAmKey: (state, getter, rootState, rootGetters) =>
      rootGetters['selectConfigByName']('AMKEY'),
    selectClassificationLocation: (state) => (id) => {
      const classification = state.classifications.find((c) => c._id === id)
      return (
        classification?.location ||
        (classification?.locations?.length && classification?.locations[0]) ||
        null
      )
    },
    selectShowHighlight(state, getters, rootState, rootGetters) {
      const subcategories = rootGetters['insightsv2/selectNonOtherCategories'].filter((c) => c.path)
      if (
        subcategories.find((sc) => sc._id === state.detailFilters.category) &&
        state.detailFilters.sentiment
      ) {
        return state.detailFilters.category
      } else {
        return false
      }
    },
    selectDetailFilters(state) {
      return state.detailFilters
    },
    selectSkip(state) {
      return state.skip
    },
    selectPopulatedClassifications(state) {
      return state.populatedClassifications
    },
    selectCount(state) {
      return state.count
    },
    selectLimit(state) {
      return state.limit
    },
    selectSentiment(state) {
      return state.detailFilters.sentiment
    },
    selectDetailsFilterCategory(state, getters, rootState, rootGetters) {
      return rootGetters['insightsv2/selectCategory'](state.detailFilters.category)
    },
    selectGranularity(state, getters) {
      const dateRange = getters.selectDetailFilters.dateRange
      const dateSpread = moment(dateRange[1]).diff(moment(dateRange[0]), 'd')
      let granularity
      if (dateSpread <= 14) granularity = 'day'
      else if (dateSpread <= 98) granularity = 'week'
      else if (dateSpread <= 420) granularity = 'month'
      else granularity = 'year'
      return granularity
    },
    selectMentions(state) {
      return state.mentions
    },
    selectAllMentions(state) {
      if (!state.detailFilters.sentiment) return state.mentions

      // Get the date range from filters
      const startDate = new Date(state.detailFilters.dateRange[0])
      const endDate = new Date(state.detailFilters.dateRange[1])

      // Create a map of all dates in range with zero counts
      const allDatesMap = {}
      for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
        const dateStr = date.toISOString().split('T')[0]
        allDatesMap[dateStr] = {
          NEGATIVE: 0,
          POSITIVE: 0
        }
      }

      // Combine mentions with oppositeSentimentMentions and ensure all dates are included
      return Object.keys(allDatesMap).reduce((combinedMentions, date) => {
        if (state.mentions[date] && state.oppositeSentimentMentions[date]) {
          combinedMentions[date] = {
            NEGATIVE:
              state.mentions[date].NEGATIVE + state.oppositeSentimentMentions[date].NEGATIVE,
            POSITIVE: state.mentions[date].POSITIVE + state.oppositeSentimentMentions[date].POSITIVE
          }
        } else if (state.mentions[date]) {
          combinedMentions[date] = { ...state.mentions[date] }
        } else if (state.oppositeSentimentMentions[date]) {
          combinedMentions[date] = { ...state.oppositeSentimentMentions[date] }
        } else {
          // If no mentions for this date, use the zero counts
          combinedMentions[date] = allDatesMap[date]
        }
        return combinedMentions
      }, {})
    },
    selectFeedbackCount(state) {
      return state.feedbackCount
    },
    selectPositiveClassificationsByCategory(state) {
      return state.positiveClassificationsByCategory
    },
    selectNegativeClassificationsByCategory(state) {
      return state.negativeClassificationsByCategory
    },
    selectCategoryPercentageNegative: (state, getters) => (id) => {
      if (!state.feedbackCount) return 0
      return (
        (
          (state.negativeClassificationsByCategory[id]?.classifications?.length ?? 0) /
          state.feedbackCount
        ).toFixed(2) * 100
      )
    },
    selectCategoryIssueCount: (state) => (id) => {
      return (
        state.negativeClassificationsByCategory[id]?.classifications?.reduce((acc, curr) => {
          acc += curr.negativeMentionCount
          return acc
        }, 0) ?? 0
      )
    },
    selectLocationFeedbackCount: (state) => (id) => {
      const counts = state.feedbackCountBreakdown[String(id)]
      return counts ? counts.reviewCount + counts.surveyCount : 0
    },
    selectLocationMiniHeatmapSeries(state) {
      const categoryId = state.detailFilters.category
      if (!categoryId) return

      // Return empty if no feedbackBreakdown data
      if (!state.feedbackBreakdown?.length) return

      // Check if any location has negative mentions for the selected category
      const hasNegativeMentions = state.feedbackBreakdown.some((item) =>
        item.categoryBreakdown?.some(
          (cat) => cat.categoryId === categoryId && cat.negativeMentionCount > 0
        )
      )
      if (!hasNegativeMentions) return

      // Filter out positive sentiment if that's the current filter
      if (state.detailFilters.sentiment === 'POSITIVE') return

      // Filter out items without locationId (like "All" entry) and process location data
      const locationData = state.feedbackBreakdown
        .filter((item) => item.locationId) // Only items with locationId
        .map((item) => {
          // Find the category breakdown for the selected category
          const categoryBreakdown = item.categoryBreakdown?.find(
            (cat) => cat.categoryId === categoryId
          )

          // Count negative mentions for this category at this location
          const negativeMentionCount = categoryBreakdown?.negativeMentionCount || 0
          const totalFeedbackCount = item.feedbackCount || 0

          return {
            locationId: item.locationId,
            label: item.label,
            count: negativeMentionCount,
            totalCount: totalFeedbackCount,
            data:
              totalFeedbackCount > 0
                ? Math.ceil((negativeMentionCount / totalFeedbackCount) * 100)
                : 0
          }
        })

      // If no locations have incidents, return empty
      if (!locationData.length) return

      // Sort by percentage (descending) and take top 8
      const top8 = locationData.sort((a, b) => b.data - a.data).slice(0, 8)

      return [
        {
          name: '',
          locationIds: top8.map((el) => el.locationId),
          data: top8.map((el) => el.data),
          counts: top8.map((el) => el.count),
          totalCounts: top8.map((el) => el.totalCount)
        }
      ]
    },
    selectSummaries: (state) => state.summaries,
    selectWordCloud: (state) => {
      const quantity = state.wordCloudQuantity
      const halfQuantity = quantity / 2
      const posWC = state.wordClouds.positiveWordCloud || []
      const negWC = state.wordClouds.negativeWordCloud || []

      let wordcloud = []
      if (posWC.length > halfQuantity && negWC.length > halfQuantity) {
        wordcloud = [...posWC.slice(0, halfQuantity), ...negWC.slice(0, halfQuantity)]
      } else if (posWC.length < halfQuantity && negWC.length < halfQuantity) {
        wordcloud = [...posWC, ...negWC]
      } else if (posWC?.length > halfQuantity) {
        const negQuantity = negWC.length
        wordcloud = [...negWC, ...posWC.slice(0, quantity - negQuantity)]
      } else if (negWC?.length > halfQuantity) {
        const posQuantity = posWC.length
        wordcloud = [...posWC, ...negWC.slice(0, quantity - posQuantity)]
      }

      const totalCount = wordcloud.reduce((acc, curr) => {
        acc += curr.count
        return acc
      }, 0)
      return wordcloud.reduce((acc, curr) => {
        acc.push({
          category: curr.word,
          value: ((curr.count / totalCount) * 100).toFixed(2),
          sentiment: curr.sentiment
        })
        return acc
      }, [])
    }
  },
  mutations: {
    SET_DETAIL_FILTERS(state, detailFilters) {
      state.detailFilters = detailFilters
    },
    SET_DETAILED_CLASSIFICATIONS(state, populatedClassifications) {
      state.populatedClassifications = populatedClassifications
    },
    SET_SKIP(state, skip) {
      state.skip = skip
    },
    SET_COUNT(state, count) {
      state.count = count
    },
    SET_MENTIONS(state, mentions) {
      state.mentions = mentions
    },
    SET_FEEDBACK_COUNT(state, feedbackCount) {
      state.feedbackCount = feedbackCount
    },
    SET_SUMMARIES(state, summaries) {
      state.summaries = summaries
    },
    SET_WORDCLOUDS(state, wordClouds) {
      state.wordClouds = wordClouds
    },
    SET_FEEDBACK_BREAKDOWN(state, breakdown) {
      state.feedbackBreakdown = breakdown
    },
    SET_TOTAL_FEEDBACK_COUNT(state, count) {
      state.totalFeedbackCount = count
    },
    SET_POSITIVE_CLASSIFICATIONS_BY_CATEGORY(state, classifications) {
      state.positiveClassificationsByCategory = classifications
    },
    SET_NEGATIVE_CLASSIFICATIONS_BY_CATEGORY(state, classifications) {
      state.negativeClassificationsByCategory = classifications
    },
    SET_FEEDBACK_COUNT_BREAKDOWN(state, feedbackCountBreakdown) {
      state.feedbackCountBreakdown = feedbackCountBreakdown
    }
  },
  actions: {
    async applyDetailFilters({ commit, getters, dispatch }, detailFilters) {
      const { selectDetailFilters } = getters
      dispatch('clearPopulatedClassificationstate')
      commit('SET_DETAIL_FILTERS', detailFilters)
    },

    async clearPopulatedClassificationstate({ commit }) {
      commit('SET_SKIP', 0)
      commit('SET_DETAILED_CLASSIFICATIONS', [])
    },

    async formatDetailFilters({ getters, rootState, rootGetters }) {
      const { activeCompany } = rootState.company
      const filters = getters.selectDetailFilters
      const payloadFilters = {
        createdAtRange: filters.dateRange || [
          moment().startOf('d').subtract(30, 'd').utc().format(),
          moment().endOf('day').utc().format()
        ]
      }
      if (activeCompany._id === 'ALL_COMPANIES') {
        if (filters.companies) {
          payloadFilters.companyIds = Array.isArray(filters.companies)
            ? filters.companies
            : [filters.companies]
        } else {
          payloadFilters.companyIds = rootGetters['company/selectInsightsCompanies'].map(
            (c) => c._id
          )
        }
      } else {
        payloadFilters.companyIds = [activeCompany._id]
      }
      if (filters.locations) {
        payloadFilters.locationIds = Array.isArray(filters.locations)
          ? filters.locations
          : [filters.locations]
      } else {
        /*payloadFilters.locationIds = rootGetters['location/selectInsightsLocations']
          .filter((l) => !l.hideFromReports && !l.disabled)
          .map((l) => l._id)*/
      }

      if (filters.groups && filters.groups.length) {
        payloadFilters.groupIds = Array.isArray(filters.groups) ? filters.groups : [filters.groups]
      }

      if (filters.sentiment) {
        payloadFilters.sentiment = filters.sentiment
      }
      if (filters.category) {
        // if parent category, pass all child id's too
        const isSubcategory = rootGetters['insightsv2/selectCategory'](filters.category)?.path
        if (isSubcategory) {
          payloadFilters.categoryIds = [filters.category]
        } else {
          payloadFilters.categoryIds = rootGetters['insightsv2/selectNonOtherCategories'].reduce(
            (acc, curr) => {
              if (curr.path === filters.category || curr._id === filters.category)
                [acc.push(curr._id)]
              return acc
            },
            []
          )
        }
      }
      if (filters.sources?.length) {
        payloadFilters.sources = filters.sources
      }
      return payloadFilters
    },

    async fetchSummaries({ commit, dispatch, getters }) {
      const filters = getters.selectDetailFilters
      const response = await InsightsService.fetchSummaries({
        filters: await dispatch('formatDetailFilters'),
        ...(filters.keywords?.length ? { search: filters.keywords } : {})
      })
      const {
        positiveSummary,
        negativeSummary,
        positiveWordCloud,
        negativeWordCloud,
        summariesUnavailable
      } = response.body.data
      const summaries = { positiveSummary, negativeSummary, summariesUnavailable }
      const wordClouds = { positiveWordCloud, negativeWordCloud }
      commit('SET_SUMMARIES', summaries)
      commit('SET_WORDCLOUDS', wordClouds)
    },

    async fetchFeedbackData({ dispatch, commit, getters }, { loadMore = false } = {}) {
      const filters = getters.selectDetailFilters
      const response = await InsightsService.fetchFeedbackData({
        filters: await dispatch('formatDetailFilters'),
        ...(filters.keywords?.length ? { search: filters.keywords } : {}),
        limit: getters.selectLimit,
        skip: getters.selectSkip
      })

      // Only append data if loadMore is true, otherwise replace the data
      if (loadMore) {
        commit(
          'SET_DETAILED_CLASSIFICATIONS',
          getters.selectPopulatedClassifications.concat(response.body.data.feedback)
        )
        commit('SET_SKIP', getters.selectPopulatedClassifications.length)
      } else {
        commit('SET_DETAILED_CLASSIFICATIONS', response.body.data.feedback)
        commit('SET_SKIP', response.body.data.feedback.length)
      }

      commit('SET_COUNT', response.body.data.count)
    },

    async fetchBreakdown({ dispatch, commit, getters, rootGetters }) {
      const filters = getters.selectDetailFilters
      const granularity = getters.selectGranularity
      const dateRange = filters.dateRange
      const categories = rootGetters['insightsv2/selectNonOtherCategories']

      // Initialize category classifications
      let positiveClassificationsByCategory = {}
      let negativeClassificationsByCategory = {}
      categories.forEach((cat) => {
        positiveClassificationsByCategory[cat._id] = { name: cat.name, classifications: [] }
        negativeClassificationsByCategory[cat._id] = { name: cat.name, classifications: [] }
      })

      const response = await InsightsService.fetchBreakdown({
        filters: await dispatch('formatDetailFilters'),
        ...(filters.keywords?.length ? { search: filters.keywords } : {}),
        getAllData: true
      })

      // Transform feedbackBreakdown into the desired format
      const locationCounts = response.body.data.feedbackBreakdown.reduce((acc, item) => {
        acc[item.locationId] = {
          surveyCount: item.feedbackCount,
          reviewCount: 0
        }
        return acc
      }, {})

      let mentions = {}
      response.body.data.mentionsOverTime.forEach((item) => {
        const date =
          granularity === 'day'
            ? item.date
            : granularity === 'week'
            ? moment(item.date).startOf('w').format('YYYY-MM-DD')
            : granularity === 'month'
            ? moment(item.date).startOf('M').format('YYYY-MM-DD')
            : moment(item.date).startOf('year').format('YYYY-MM-DD')

        if (mentions[date]) {
          mentions[date].POSITIVE += item.positiveMentionCount || 0
          mentions[date].NEGATIVE += item.negativeMentionCount || 0
        } else {
          mentions[date] = {
            POSITIVE: item.positiveMentionCount || 0,
            NEGATIVE: item.negativeMentionCount || 0
          }
        }
      })

      response.body.data.feedbackBreakdown.forEach((item, index) => {
        if (index !== 0) {
          item.categoryBreakdown.forEach((category) => {
            if (!rootGetters['insightsv2/selectOtherCategoryIds'].includes(category.categoryId)) {
              if (category.positiveMentionCount > 0) {
                const classification = {
                  category: category.categoryId,
                  sentiment: 'POSITIVE',
                  locations: [item.locationId],
                  text: category.categoryName,
                  positiveMentionCount: category.positiveMentionCount
                }
                positiveClassificationsByCategory[category.categoryId].classifications.push(
                  classification
                )
              }
              if (category.negativeMentionCount > 0) {
                const classification = {
                  category: category.categoryId,
                  negativeMentionCount: category.negativeMentionCount,
                  sentiment: 'NEGATIVE',
                  locations: [item.locationId],
                  text: category.categoryName
                }
                negativeClassificationsByCategory[category.categoryId].classifications.push(
                  classification
                )
              }
            }
          })
        }
      })

      let startDate = moment(dateRange[0]).startOf(granularity)
      const finalDate = moment(dateRange[1]).startOf(granularity)
      let completeMentions = {}

      while (startDate.isBefore(finalDate) || startDate.isSame(finalDate)) {
        const formattedDate = startDate.format('YYYY-MM-DD')
        completeMentions[formattedDate] = mentions[formattedDate] || {
          POSITIVE: 0,
          NEGATIVE: 0
        }
        startDate.add(1, granularity)
      }

      const feedbackCount = response.body.data.feedbackBreakdown.reduce(
        (acc, item, index) => (index === 0 ? acc : acc + item.feedbackCount),
        0
      )

      commit('SET_POSITIVE_CLASSIFICATIONS_BY_CATEGORY', positiveClassificationsByCategory)
      commit('SET_NEGATIVE_CLASSIFICATIONS_BY_CATEGORY', negativeClassificationsByCategory)
      commit('SET_FEEDBACK_BREAKDOWN', response.body.data.feedbackBreakdown)
      commit('SET_TOTAL_FEEDBACK_COUNT', response.body.data.totalFeedbackCount)
      commit('SET_MENTIONS', completeMentions)
      commit('SET_FEEDBACK_COUNT_BREAKDOWN', locationCounts)
      commit('SET_FEEDBACK_COUNT', feedbackCount)
    }
  }
}
